import { put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import {
  fetchKnoOrganisationFunctionSetDefault,
  fetchUserOrganisationsUpdate,
} from 'APIPackage/AccountModule/services/userApi';
import { UserOrgTypesEnum } from 'APIPackage/AccountModule/enums/UserOrgTypesEnum';
import {
  fetchProsecutorOrganisationsByKno,
  fetchProsecutorOrganisationsSearchByOgrn,
  fetchProsecutorOrganisationsChildren,
  fetchProsecutorOrganisationsSearch,
  fetchProsecutorOrganisationsSearchByDistrict,
} from 'APIPackage/CatalogsModule/services/prosecutorOrganisationsApi';
import { IProsecutorOrganisationsChildrenResponse } from 'APIPackage/CatalogsModule/interfaces/IProsecutorOrganisationsChildrenResponse';
import {
  getUserRoleIsAdmin,
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsProsecutorOrSuperProsecutorOrOmbudsman,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  accountOrganizationsGetSuccess,
  accountOrganizationsWithDistrictGetSuccess,
} from 'CorePackage/AccountModule/actions/accountActions';
import { AccountActionsEnum } from 'CorePackage/AccountModule/enums/AccountActionsEnum';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { USER_INFO_KNO_BLANK, USER_INFO_PROSECUTOR_BLANK } from 'CorePackage/MainModule/constants/notices';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { IKnoOrganisationsChildrenResponse } from 'APIPackage/CatalogsModule/interfaces/IKnoOrganisationsChildrenResponse';
import {
  fetchKnoErvkOrganizationsSearch,
  fetchKnoOrganisationsChildren,
  fetchKnoErvkOrganisationsChildrenWithAdminLevel,
  fetchKnoErvkOrganisationsChildrenWithKnoLevelCode,
} from 'APIPackage/CatalogsModule/services/knoOrganisationsApi';
import { getCatalogKnoAdminLevelsSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IUserOrganisationsResponse } from 'APIPackage/AccountModule/interfaces/IUserOrganisationsResponse';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { dictionariesKnoOrganizationsItemUpdate } from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getKnmDataCreated } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';

import {
  userInfoUpdateOrganizations,
  userInfoSetDefaultFunction,
  userInfoSetOrganisations,
  userInfoProsecutorOrganizationsGetRequest,
  userInfoProsecutorOrganizationsGetSuccess,
  userInfoProsecutorOrganizationsGetFailure,
  userInfoProsecutorOrganizationsNextPageGetRequest,
  userInfoProsecutorOrganizationsNextPageGetSuccess,
  userInfoProsecutorOrganizationsNextPageGetFailure,
  userInfoProsecutorsByOrganizationsGetSuccess,
  userInfoProsecutorsByOrganizationsGet,
} from '../actions/userInfoActions';
import { UserInfoActionsEnum } from '../enums/UserInfoActionsEnum';
import {
  getUserInfoKnoOrganisations,
  getUserInfoOrganisationsPage,
  getUserInfoOrganisationsParentId,
  getUserInfoOrganisationsSearchQuery,
  getUserInfoOrganisationsSearchCriteria,
  getUserInfoOrganisationsAdminLevelCode,
  getUserInfoProsecutorOrganizationsDistrictId,
  getUserInfoProsecutorOrganizationsSearchQuery,
  getUserInfoProsecutorOrganizationsParentId,
  getUserInfoProsecutorOrganizationsPage,
  getUserInfoProsecutorOrganizationsSearchOgrn,
} from '../selectors/userInfoSelectors';
import { httpUpdateOrganizationsError } from '../constants/httpUpdateOrganizationsError';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { fetchDictionaryKnoOrganisationsChildren } from '../../../APIPackage/DictionariesModule/services/dictionariesApi';

const PAGE_SIZE = '500';

export function* watchUserInfoSaga() {
  yield takeEvery(UserInfoActionsEnum.USER_INFO_GET_KNO_ADMIN_LEVELS, userInfoGetKnoAdminLevelsSaga);
  yield takeEvery(UserInfoActionsEnum.USER_INFO_UPDATE_ORGANIZATIONS, userInfoUpdateOrganizationsSaga);
  yield takeEvery(UserInfoActionsEnum.USER_INFO_SET_DEFAULT_FUNCTION, userInfoSetDefaultFunctionSaga);
  yield takeEvery(UserInfoActionsEnum.USER_INFO_PROSECUTOR_ORGANIZATIONS_GET, userInfoProsecutorOrganizationsGetSaga);
  yield takeEvery(
    UserInfoActionsEnum.USER_INFO_PROSECUTOR_ORGANIZATIONS_NEXT_PAGE_GET,
    userInfoProsecutorOrganizationsNextPageGetSaga,
  );
  yield takeEvery(UserInfoActionsEnum.USER_INFO_GET_ORGANISATIONS, userInfoGetOrganisationsSaga);
  yield takeEvery(UserInfoActionsEnum.USER_INFO_GET_ORGANISATIONS_NEXT_PAGE, userInfoGetOrganisationsNextPageSaga);
  yield takeEvery(AccountActionsEnum.ACCOUNT_ORGANIZATIONS_GET_SUCCESS, userInfoCheckOrganizationSaga);
  yield takeEvery(UserInfoActionsEnum.USER_INFO_PROSECUTOR_BY_ORGANIZATIONS_GET, fetchProsecutorOrganisationsByKnoSaga);
}

function* userInfoGetKnoAdminLevelsSaga() {
  yield put(catalogGet(CommonCatalogsNamesEnum.KNO_ADMIN_LEVELS));
}

function* userInfoCheckOrganizationSaga(action: ActionType<typeof accountOrganizationsGetSuccess>) {
  const { KNO, PROSECUTOR } = action.payload.data;

  const state: IRootState = yield select();
  const isAdmin: boolean | undefined = getUserRoleIsAdmin(state);

  yield put(catalogGet(CommonCatalogsNamesEnum.KNM_INSPECTORS));
  if (!isAdmin) {
    if (KNO.length === 0) {
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.WARNING,
          messageType: MessageTypesEnum.PROSECUTOR_SELECTION,
          renderContent: USER_INFO_PROSECUTOR_BLANK,
        }),
      );
    }
    if (PROSECUTOR.length === 0) {
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.WARNING,
          messageType: MessageTypesEnum.KNO_SELECTION,
          renderContent: USER_INFO_KNO_BLANK,
        }),
      );
    }
  }
}

function* userInfoUpdateOrganizationsSaga(action: ActionType<typeof userInfoUpdateOrganizations>) {
  function* funcTry() {
    const { knoErvkIds, prosecIds, knoDefaultErvkId, prosecDefaultId } = action.payload;

    const userOrganizations: IUserOrganisationsResponse = yield fetchUserOrganisationsUpdate([
      {
        organizationType: UserOrgTypesEnum.KNO,
        organizationInfo: {
          defaultOrganizationErvkId: knoDefaultErvkId,
          organizationErvkIds: knoErvkIds,
        },
      },
      {
        organizationType: UserOrgTypesEnum.PROSECUTOR,
        organizationInfo: {
          defaultOrganizationId: prosecDefaultId,
          organizationIds: prosecIds,
        },
      },
    ]);

    yield put(accountOrganizationsGetSuccess(userOrganizations));
    yield put(accountOrganizationsWithDistrictGetSuccess(userOrganizations));
    yield put(dictionariesKnoOrganizationsItemUpdate());
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      if (err.message?.includes(httpUpdateOrganizationsError)) {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.FILE_UPLOAD,
            renderContent:
              'Для корректной работы портала Вам необходимо выбрать и сохранить "Организацию" и "Связанную организацию" (Прокуратуру).',
          }),
        );
      } else {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.FILE_UPLOAD,
            renderContent: err.message,
          }),
        );
      }
    },
  });
}

function* userInfoSetDefaultFunctionSaga(action: ActionType<typeof userInfoSetDefaultFunction>) {
  yield sagaWrapper({
    *funcTry() {
      const { knoId, functionId } = action.payload;

      yield fetchKnoOrganisationFunctionSetDefault({
        knoId,
        functionId,
      });
    },
  });
}

function* userInfoGetOrganisationsSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getUserInfoOrganisationsParentId(state);
    const searchQuery = getUserInfoOrganisationsSearchQuery(state);
    const searchCriteria = getUserInfoOrganisationsSearchCriteria(state);
    const knoOrganizationAdminLevelsSelectOptions = getCatalogKnoAdminLevelsSelectOptions(state);
    const adminLevel = getUserInfoOrganisationsAdminLevelCode(state);
    const shouldUseERVKParamTables = isLaterThanAdminSetting(
      getKnmDataCreated(state),
      getAdminFetchSettings(state),
      AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM,
    );

    if (!adminLevel && !knoOrganizationAdminLevelsSelectOptions.length) {
      return;
    }
    if (searchQuery) {
      let res: IKnoOrganisationsChildrenResponse;

      if (shouldUseERVKParamTables) {
        res = yield fetchKnoErvkOrganizationsSearch({
          query: searchQuery,
          adminLevel: adminLevel || knoOrganizationAdminLevelsSelectOptions[0]?.code,
          searchCriteria,
          order: 'fullName',
          page: '0',
          size: PAGE_SIZE,
        });
      } else {
        res = yield fetchKnoOrganisationsChildren({
          adminLevel: adminLevel || knoOrganizationAdminLevelsSelectOptions[0]?.code,
          order: 'fullName',
          page: '0',
          size: PAGE_SIZE,
        });
      }

      yield put(userInfoSetOrganisations(res.list, res.page.toString(), res.pageCount, res.totalCount));
    } else {
      const res: IKnoOrganisationsChildrenResponse = yield shouldUseERVKParamTables
        ? fetchKnoErvkOrganisationsChildrenWithAdminLevel({
            knoLevelCode: adminLevel || knoOrganizationAdminLevelsSelectOptions[0]?.code,
            id,
            order: 'fullName',
            page: '0',
            size: PAGE_SIZE,
          })
        : fetchKnoErvkOrganisationsChildrenWithKnoLevelCode({
            knoLevelCode: adminLevel || knoOrganizationAdminLevelsSelectOptions[0]?.code,
            id,
            order: 'fullName',
            page: '0',
            size: PAGE_SIZE,
          });

      yield put(userInfoSetOrganisations(res.list, res.page.toString(), res.pageCount, res.totalCount));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* userInfoGetOrganisationsNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const page = getUserInfoOrganisationsPage(state);
    const currentList = getUserInfoKnoOrganisations(state);
    const nextPage = (+page + 1).toString();
    const adminLevel = getUserInfoOrganisationsAdminLevelCode(state);
    const knoOrganizationAdminLevelsSelectOptions = getCatalogKnoAdminLevelsSelectOptions(state);
    const shouldUseERVKParamTables = isLaterThanAdminSetting(
      getKnmDataCreated(state),
      getAdminFetchSettings(state),
      AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM,
    );

    if (!adminLevel) {
      return;
    }

    let res: IKnoOrganisationsChildrenResponse;

    if (shouldUseERVKParamTables) {
      res = yield fetchDictionaryKnoOrganisationsChildren({
        adminLevel,
        order: 'fullName',
        page: nextPage,
        size: PAGE_SIZE,
      });
    } else {
      res = yield fetchKnoOrganisationsChildren({
        adminLevel: adminLevel || knoOrganizationAdminLevelsSelectOptions[0]?.code,
        order: 'fullName',
        page: nextPage,
        size: PAGE_SIZE,
      });
    }

    yield put(
      userInfoSetOrganisations([...currentList, ...res.list], res.page.toString(), res.pageCount, res.totalCount),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* fetchProsecutorOrganisationsGetSaga(
  isProsecutorOrSuperProsecutor: boolean | undefined,
  districtId: number | undefined,
  searchQuery: string | undefined,
  searchOgrn: string | undefined,
  parentId: string | undefined,
  page: string,
  state: IRootState,
) {
  let response: IProsecutorOrganisationsChildrenResponse | undefined;

  if (isProsecutorOrSuperProsecutor) {
    if (searchOgrn) {
      response = yield fetchProsecutorOrganisationsSearchByOgrn({
        searchOgrn,
        order: 'fullName',
        page,
        size: PAGE_SIZE,
      });
    } else if (!parentId) {
      response = yield fetchProsecutorOrganisationsSearchByDistrict({
        districtId,
        query: searchQuery,
        order: 'fullName',
        page,
        size: PAGE_SIZE,
      });
    } else {
      response = yield fetchProsecutorOrganisationsChildren({
        id: parentId,
        enabled: true,
        order: 'fullName',
        page,
        size: PAGE_SIZE,
      });
    }
  } else if (searchQuery) {
    response = yield fetchProsecutorOrganisationsSearch({
      state,
      query: searchQuery,
      enabled: true,
      order: 'fullName',
      page,
      size: PAGE_SIZE,
    });
  } else {
    response = yield fetchProsecutorOrganisationsChildren({
      id: parentId,
      enabled: true,
      order: 'fullName',
      page,
      size: PAGE_SIZE,
    });
  }
  if (response) {
    const pageIndex = Number(page);

    if (pageIndex > 0) {
      yield put(userInfoProsecutorOrganizationsNextPageGetSuccess(response));
    } else {
      yield put(userInfoProsecutorOrganizationsGetSuccess(response));
    }
  }
}

function* userInfoProsecutorOrganizationsGetSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const isProsecutorOrSuperProsecutorOrOmbudsman:
      | boolean
      | undefined = getUserRoleIsProsecutorOrSuperProsecutorOrOmbudsman(state);
    const districtId = getUserInfoProsecutorOrganizationsDistrictId(state);

    yield put(userInfoProsecutorOrganizationsGetRequest());
    const searchQuery = getUserInfoProsecutorOrganizationsSearchQuery(state);
    const parentId = getUserInfoProsecutorOrganizationsParentId(state);
    const searchOgrn = getUserInfoProsecutorOrganizationsSearchOgrn(state);

    yield fetchProsecutorOrganisationsGetSaga(
      isProsecutorOrSuperProsecutorOrOmbudsman,
      districtId,
      searchQuery,
      searchOgrn,
      parentId,
      '0',
      state,
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(userInfoProsecutorOrganizationsGetFailure());
    },
  });
}

function* userInfoProsecutorOrganizationsNextPageGetSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const isProsecutorOrSuperProsecutor: boolean | undefined = getUserRoleIsProsecutorOrSuperProsecutor(state);
    const districtId = getUserInfoProsecutorOrganizationsDistrictId(state);

    yield put(userInfoProsecutorOrganizationsNextPageGetRequest());
    const searchQuery = getUserInfoProsecutorOrganizationsSearchQuery(state);
    const parentId = getUserInfoProsecutorOrganizationsParentId(state);
    const searchOgrn = getUserInfoProsecutorOrganizationsSearchOgrn(state);
    const page = getUserInfoProsecutorOrganizationsPage(state);
    const nextPage = (+page + 1).toString();

    yield fetchProsecutorOrganisationsGetSaga(
      isProsecutorOrSuperProsecutor,
      districtId,
      searchQuery,
      searchOgrn,
      parentId,
      nextPage,
      state,
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(userInfoProsecutorOrganizationsNextPageGetFailure());
    },
  });
}

function* fetchProsecutorOrganisationsByKnoSaga(params: ActionType<typeof userInfoProsecutorsByOrganizationsGet>) {
  function* funcTry() {
    const { ids } = params.payload;

    const userOrganizations: IUserOrganisation[] = yield fetchProsecutorOrganisationsByKno(ids);

    yield put(userInfoProsecutorsByOrganizationsGetSuccess(userOrganizations));
  }

  yield sagaWrapper({
    funcTry,
  });
}
